import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, Clock } from "lucide-react";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Reveal } from "@/lib/Reveal";
import { BUSINESS } from "@/lib/data";
import { POSTS } from "@/lib/blog";

export default function BlogIndex() {
  useEffect(() => {
    document.title = "Plumbing & Heating Advice — From the Van | Teign Gas";
    const d = document.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", "Plain-English plumbing and heating advice from Teign Gas engineers — boiler servicing, breakdown warning signs, power flushing, heat pumps and plumbing tips for South Devon homes.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="blog-index-page" className="bg-obsidian text-neutral-100 overflow-x-clip min-h-screen">
      <div className="grain-overlay" />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="blog-index-logo" className="flex items-center shrink-0">
            <img src="/images/logo-flame.png" alt="Teign Gas" className="h-9 w-9 sm:hidden mix-blend-screen" />
            <img src="/images/logo-mark.png" alt="Teign Gas — Service, Repair, Install" className="hidden sm:block h-9 w-auto mix-blend-screen" />
          </Link>
          <div className="flex items-center gap-3">
            <a href={BUSINESS.phoneHref} className="hidden sm:flex items-center gap-2 font-mono text-xs tracking-wider text-neutral-300 border border-neutral-700 px-4 py-2.5 rounded-sm hover:border-torch hover:text-torch transition-colors">
              <Phone className="w-3.5 h-3.5" /> {BUSINESS.phone}
            </a>
            <Link to="/#booking" data-testid="blog-index-book-btn" className="bg-torch hover:bg-torch-hover text-white font-display font-bold text-sm uppercase tracking-wide px-5 py-2.5 rounded-sm transition-all whitespace-nowrap">
              Book Online
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-torch font-semibold">From the van</p>
            <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white mt-4">
              Plumbing & heating,<br />explained plainly.
            </h1>
            <p className="mt-5 max-w-xl text-neutral-400 leading-relaxed">
              Honest advice from 15+ years on the tools — what things should cost,
              what the warning signs mean, and when you actually need to call someone.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
            {POSTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08} className="h-full">
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-index-card-${p.slug}`}
                  className="group flex flex-col h-full bg-surface border border-neutral-800 hover:border-torch rounded-sm overflow-hidden transition-all hover:-translate-y-1"
                >
                  <div className="overflow-hidden aspect-[16/10]">
                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-torch uppercase">{p.date}</span>
                      <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-wider text-neutral-500 uppercase"><Clock className="w-3 h-3" /> {p.readTime}</span>
                    </div>
                    <h2 className="font-display font-bold text-lg text-white leading-snug flex-1">{p.title}</h2>
                    <p className="mt-3 text-sm text-neutral-400 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-torch">
                      Read article <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />
      <WhatsAppButton />
    </div>
  );
}
